/**
 * "판매가 결정" 메뉴(감사 card-controltower#341, 사장 요청 2026-10-29)의 API 계약 — 백엔드(감사 #340)와 같은 계약.
 *
 *   GET  /api/cards/decision-list?expansion&q&game&rarity(쉼표로 여럿)&unsetOnly&page&size(≤200) → DecisionListResponse
 *   GET  /api/cards/{id}/market-items                                          → DecisionMarketItems
 *   PUT  /api/cards/{id}/decided-price  { price, requestId }                   → DecidedPriceResult
 *   POST /api/cards/decided-price/bulk  { items: [{ cardId, price, requestId }] } (≤200) → 항목별 DecidedPriceResult
 */

/** 시장(타 샵) 가격 요약 — 목록 줄의 market · 판매처 목록의 summary. */
export interface DecisionMarketSummary {
  min:       number | null;
  avg:       number | null;
  median:    number | null;
  max:       number | null;
  shopCount: number | null;
  /** 목록 줄의 market 에만 온다(판매처 목록은 바깥 collectedAt). */
  collectedAt?: string | null;
}

export interface DecisionListItem {
  cardId:            number;
  sellerProductCode: string | null;
  productName:       string;
  imageUrl:          string | null;
  rarity:            string | null;
  /** 포켓몬 미러 계열(몬스터볼 · 마스터볼 …) — 아니면 null · ''. 옛 응답이 boolean 이어도 읽는다. */
  mirror:            string | boolean | null;
  expansionCode:     string | null;
  /** 네이버 가격 — 지금 네이버 판매가(CT 기준가). */
  currentPrice:      number | null;
  isManual:          boolean;
  /** 판매가를 이 화면에서 정한 시각 — null 이면 '미설정'. */
  decidedAt:         string | null;
  /** PriceHub 가 준 값(hubPriceSource 가 매장이면 '<매장> 가격', 공통이면 'PriceHub 가격'). */
  hubPrice:          number | null;
  hubPriceSource:    string | null;
  market:            DecisionMarketSummary | null;
  /** PriceHub 쪽 상태 경고(감사 card-controltower#362 — 막지 않고 알린다). 옛 서버는 없음. */
  hubWarnings?:      string[];
  /** 사람이 정한 가격 보호 중(백엔드 PR #77 DecisionListResponse.Item.priceHeld). 옛 서버는 없음 — 그때만 화면이 카드 캐시에서 싣는다. */
  priceHeld?:        boolean;
}

/**
 * PriceHub 쪽 상태 경고(백엔드 PR #71, 사장 결정 2026-10-29 #362 안 B — 결정은 막지 않고 경고만).
 * LOCKED = PriceHub 판매가 고정 중 · PENDING_REQUEST = PriceHub 매장가 요청 대기 중 · PENDING_CONFIRM = 확정 대기 알림 있음.
 */
export type HubWarning = 'LOCKED' | 'PENDING_REQUEST' | 'PENDING_CONFIRM';

export interface DecisionListResponse {
  items: DecisionListItem[];
  total: number;
}

export interface DecisionListParams {
  expansion?: string;
  q?:         string;
  /** 게임(CardType 이름, 예: POKEMON) — 백엔드 v0.46.3(감사 #449)부터 서버가 거른다(감사 #491). */
  game?:      string;
  /** 레어도 여럿(하나라도 맞으면) — 쉼표로 묶어 rarity 하나로 보낸다(감사 #491). */
  rarities?:  string[];
  unsetOnly?: boolean;
  /** 0부터(스프링 Pageable 관례). */
  page:       number;
  /** ≤ 200. */
  size:       number;
}

/** 판매처 목록 한 줄. */
export interface DecisionMarketItem {
  shop:     string | null;
  title:    string | null;
  price:    number | null;
  url:      string | null;
  imageUrl: string | null;
}

export interface DecisionMarketItems {
  collectedAt: string | null;
  items:       DecisionMarketItem[];
  summary:     DecisionMarketSummary | null;
}

export type NaverSaveStatus = 'SUCCESS' | 'FAILED' | 'UNKNOWN';

export interface DecidedPriceResult {
  cardId: number;
  price:  number;
  naver:  { status: NaverSaveStatus; message: string | null };
  hub:    { reported: boolean; message: string | null } | null;
  /** 결정 때 PriceHub 쪽 상태 경고(#362). 같은 requestId 재전송 결과는 [] 로 온다 — 화면은 목록 값을 지우지 않는다. */
  hubWarnings?: string[];
  /**
   * 서버가 저장한 결정 시각(백엔드 PR #74, 감사 card-controltower#436 · #501) — 화면 시각 대신 이 값을 쓴다. 결정가를 저장하기 전에
   * 끝난 실패(카드 없음 · 같은 요청 처리 중 · 검증 실패)는 null. 옛 서버는 키가 없다(undefined) — 그때는 화면 시각.
   */
  decidedAt?: string | null;
}

export interface DecidedPriceRequestItem {
  cardId:    number;
  price:     number;
  requestId: string;
}

export const MAX_DECISION_PAGE_SIZE = 200;
export const MAX_DECIDED_BULK = 200;

/** 화면 줄에 남기는 저장 결과. */
export interface DecidedRowResult {
  /**
   * success = 네이버 반영됨 · failed = 반영 안 됨(새 요청 가능) · unknown = 반영됐는지 모름(같은 requestId 로만 다시) ·
   * busy = 서버가 같은 요청을 아직 처리 중(409 — 잠시 다시 보내지 않는다).
   */
  kind:    'success' | 'failed' | 'unknown' | 'busy';
  price:   number;
  message: string | null;
  hub:     { reported: boolean; message: string | null } | null;
  /** 이 저장 응답이 준 PriceHub 경고(#362). */
  hubWarnings?: string[];
  /** 결정 시각(서버 값, 없으면 화면 시각 — #501). 결정이 남지 않았으면 없음. */
  decidedAt?: string;
}

// ─── 여러 장 결정 batch(백엔드 PR #97, 감사 card-controltower#649 · #819 · #832) ─────────────────────────────

/** batch 한 번의 상한(서버 400) — 락을 쥔 채 카드마다 네이버 PUT 을 직렬로 부르니 20장이면 15~25초. */
export const MAX_DECIDED_BATCH = 20;

export interface DecidedBatchItem extends DecidedPriceRequestItem {
  /** 목록의 판매자 상품코드 — 서버 카드와 다르면 그 항목은 쓰지 않는다(`store_mismatch`, 매장 바뀐 뒤 옛 목록 보호). */
  sellerProductCode?: string;
}

/** 판매자 상품코드 불일치 — 서버의 고정 오류 코드. */
export const STORE_MISMATCH = 'store_mismatch';

export interface DecidedBatchRow {
  cardId:    number;
  requestId: string;
  /** 결정가가 저장됐다(네이버 실패 · 결과 불명도 ok — result.naver.status 를 본다). */
  ok:        boolean;
  /** ok 가 아닌 사유(판매자 상품코드 불일치는 `store_mismatch`). */
  error:     string | null;
  /** 단건 응답과 같은 모양(ok 면 있음). */
  result:    DecidedPriceResult | null;
}

export interface DecidedBatchResponse {
  results:           DecidedBatchRow[];
  okCount:           number;
  failCount:         number;
  naverFailCount:    number;
  naverUnknownCount: number;
}